import { useState } from 'react';

interface TenderModalProps {
  onClose: () => void;
  onPaymentComplete: (paymentMethods: Array<{ method: string; amount: number }>) => void;
  grandTotal: number;
}

interface TenderLine {
  id: string;
  method: string;
  methodName: string;
  amount: number;
  status: 'pending' | 'approved';
  icon: string;
  color: string;
}

export default function TenderModal({ onClose, onPaymentComplete, grandTotal }: TenderModalProps) {
  const [selectedTender, setSelectedTender] = useState<string | null>(null);
  const [tenderAmount, setTenderAmount] = useState('');
  const [tenderLines, setTenderLines] = useState<TenderLine[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);

  const totalDue = grandTotal;
  const totalPaid = tenderLines.reduce((sum, line) => sum + line.amount, 0);
  const remainingBalance = Math.max(0, totalDue - totalPaid);
  const changeDue = totalPaid > totalDue ? totalPaid - totalDue : 0;

  const tenderMethods = [
    { id: 'cash', name: 'Cash', icon: 'ri-money-dollar-circle-line', color: 'bg-green-500' },
    { id: 'card', name: 'Card (EMV)', icon: 'ri-bank-card-line', color: 'bg-blue-500' },
    { id: 'mobile', name: 'Mobile Wallet', icon: 'ri-smartphone-line', color: 'bg-purple-500' },
    { id: 'voucher', name: 'Gift Voucher', icon: 'ri-gift-line', color: 'bg-pink-500' },
    { id: 'credit', name: 'Store Credit', icon: 'ri-coupon-line', color: 'bg-orange-500' },
  ];

  const handleSelectTender = (methodId: string) => {
    const method = tenderMethods.find(m => m.id === methodId);
    if (!method) return;

    setSelectedTender(methodId);
    // Auto-fill with remaining balance
    setTenderAmount(remainingBalance > 0 ? remainingBalance.toFixed(2) : '');
  };

  const handleAddTender = () => {
    if (!selectedTender || !tenderAmount) return;

    const amount = parseFloat(tenderAmount);
    const method = tenderMethods.find(m => m.id === selectedTender);
    if (!method) return;

    // Validation
    if (amount <= 0) {
      alert('Amount must be greater than 0');
      return;
    }

    // For non-cash, don't allow over-payment
    if (selectedTender !== 'cash' && amount > remainingBalance) {
      alert('Amount cannot exceed remaining balance');
      return;
    }

    if (editingLineId) {
      // Update existing line
      setTenderLines(prev => prev.map(line => 
        line.id === editingLineId 
          ? { ...line, amount, method: selectedTender, methodName: method.name, icon: method.icon, color: method.color }
          : line
      ));
      setEditingLineId(null);
    } else {
      // Add new tender line
      const newLine: TenderLine = {
        id: Date.now().toString(),
        method: selectedTender,
        methodName: method.name,
        amount,
        status: selectedTender === 'card' ? 'pending' : 'approved',
        icon: method.icon,
        color: method.color,
      };

      setTenderLines(prev => [...prev, newLine]);

      // Simulate card approval
      if (selectedTender === 'card') {
        setTimeout(() => {
          setTenderLines(prev => prev.map(line => 
            line.id === newLine.id ? { ...line, status: 'approved' } : line
          ));
        }, 2000);
      }
    }

    // Reset selection
    setSelectedTender(null);
    setTenderAmount('');
  };

  const handleEditLine = (line: TenderLine) => {
    setEditingLineId(line.id);
    setSelectedTender(line.method);
    setTenderAmount(line.amount.toString());
  };

  const handleRemoveLine = (lineId: string) => {
    if (confirm('Remove this payment?')) {
      setTenderLines(prev => prev.filter(line => line.id !== lineId));
    }
  };

  const handleCancelEdit = () => {
    setEditingLineId(null);
    setSelectedTender(null);
    setTenderAmount('');
  };

  const canFinalize = Math.abs(remainingBalance) < 0.01 && tenderLines.every(line => line.status === 'approved') && tenderLines.length > 0;

  const handleCompletePayment = () => {
    if (!canFinalize) return;

    setIsProcessing(true);
    
    setTimeout(() => {
      setIsProcessing(false);
      setShowSuccess(true);
      
      setTimeout(() => {
        // Pass payment methods to parent
        const paymentSummary = tenderLines.map(line => ({
          method: line.methodName,
          amount: line.amount
        }));
        onClose();
        onPaymentComplete(paymentSummary);
      }, 1500);
    }, 1500);
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-green-500 text-white rounded-full mx-auto mb-4">
            <i className="ri-check-line text-5xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 whitespace-nowrap">Payment Successful!</h2>
          <p className="text-gray-600 mb-4 whitespace-nowrap">Transaction completed successfully</p>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1 whitespace-nowrap">Total Paid</p>
            <p className="text-3xl font-bold text-gray-900 whitespace-nowrap">${totalPaid.toFixed(2)}</p>
            {changeDue > 0 && (
              <>
                <p className="text-sm text-gray-600 mt-3 mb-1 whitespace-nowrap">Change Due</p>
                <p className="text-2xl font-bold text-green-600 whitespace-nowrap">${changeDue.toFixed(2)}</p>
              </>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-4 whitespace-nowrap">Opening print dialog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-teal-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Split Payment Tender</h2>
            <p className="text-sm text-teal-100 whitespace-nowrap">Accept multiple payment methods for this transaction</p>
          </div>
          <button 
            onClick={onClose}
            disabled={isProcessing}
            className="w-8 h-8 flex items-center justify-center hover:bg-teal-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto flex">
          {/* Left Panel - Payment Entry */}
          <div className="flex-1 p-6 border-r border-gray-200">
            {/* Amount Display */}
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div className="p-6 bg-gray-900 text-white rounded-xl text-center">
                <p className="text-sm text-gray-400 mb-2 whitespace-nowrap">Total Due</p>
                <p className="text-4xl font-bold whitespace-nowrap">${totalDue.toFixed(2)}</p>
              </div>
              <div className={`p-6 rounded-xl text-center ${remainingBalance > 0 ? 'bg-orange-500' : 'bg-green-500'} text-white`}>
                <p className="text-sm text-white/80 mb-2 whitespace-nowrap">Remaining Balance</p>
                <p className="text-4xl font-bold whitespace-nowrap">${remainingBalance.toFixed(2)}</p>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="mb-6">
              <h3 className="text-sm font-bold text-gray-900 mb-3 whitespace-nowrap">
                {editingLineId ? 'Edit Payment Method' : 'Select Payment Method'}
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {tenderMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => handleSelectTender(method.id)}
                    disabled={isProcessing}
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                      selectedTender === method.id
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className={`w-12 h-12 flex items-center justify-center ${method.color} text-white rounded-lg mb-2 mx-auto`}>
                      <i className={`${method.icon} text-2xl`}></i>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 text-center whitespace-nowrap">{method.name}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Amount Entry */}
            {selectedTender && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
                    {editingLineId ? 'Edit Amount' : 'Enter Amount'}
                  </label>
                  <input
                    type="number"
                    value={tenderAmount}
                    onChange={(e) => setTenderAmount(e.target.value)}
                    placeholder="0.00"
                    disabled={isProcessing}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:outline-none focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    step="0.01"
                    autoFocus
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleAddTender}
                    disabled={isProcessing || !tenderAmount || parseFloat(tenderAmount) <= 0}
                    className={`flex-1 px-6 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
                      isProcessing || !tenderAmount || parseFloat(tenderAmount) <= 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer'
                    }`}
                  >
                    <i className={`${editingLineId ? 'ri-save-line' : 'ri-add-line'} mr-2`}></i>
                    {editingLineId ? 'Update Payment' : 'Add Payment'}
                  </button>
                  {editingLineId && (
                    <button
                      onClick={handleCancelEdit}
                      className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                  )}
                </div>

                {selectedTender !== 'cash' && tenderAmount && parseFloat(tenderAmount) > remainingBalance && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600 text-xl"></i>
                    <p className="text-sm text-red-800 whitespace-nowrap">Amount cannot exceed remaining balance for this payment method</p>
                  </div>
                )}
              </div>
            )}

            {!selectedTender && tenderLines.length === 0 && (
              <div className="p-8 text-center text-gray-400">
                <i className="ri-hand-coin-line text-6xl mb-3"></i>
                <p className="text-sm whitespace-nowrap">Select a payment method to begin</p>
              </div>
            )}

            {remainingBalance > 0.01 && tenderLines.length > 0 && !selectedTender && (
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
                <i className="ri-information-line text-orange-600 text-xl"></i>
                <p className="text-sm text-orange-800 whitespace-nowrap">Add another payment method to complete the transaction</p>
              </div>
            )}
          </div>

          {/* Right Panel - Tender Summary */}
          <div className="w-96 bg-gray-50 p-6 flex flex-col">
            <h3 className="text-sm font-bold text-gray-900 mb-4 whitespace-nowrap">Payment Summary</h3>
            
            {tenderLines.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <i className="ri-file-list-3-line text-6xl mb-3"></i>
                  <p className="text-sm whitespace-nowrap">No payments added yet</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                {tenderLines.map((line, index) => (
                  <div key={line.id} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 flex items-center justify-center ${line.color} text-white rounded-lg`}>
                          <i className={`${line.icon} text-xl`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">Payment {index + 1}</p>
                          <p className="text-xs text-gray-600 whitespace-nowrap">{line.methodName}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditLine(line)}
                          disabled={isProcessing}
                          className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Edit"
                        >
                          <i className="ri-edit-line text-gray-600"></i>
                        </button>
                        <button
                          onClick={() => handleRemoveLine(line.id)}
                          disabled={isProcessing}
                          className="w-7 h-7 flex items-center justify-center hover:bg-red-50 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove"
                        >
                          <i className="ri-delete-bin-line text-red-600"></i>
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-gray-900 whitespace-nowrap">${line.amount.toFixed(2)}</span>
                      {line.status === 'pending' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 whitespace-nowrap">
                          <i className="ri-loader-4-line mr-1 animate-spin"></i>
                          Pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap">
                          <i className="ri-check-line mr-1"></i>
                          Approved
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary Totals */}
            <div className="space-y-3 pt-4 border-t-2 border-gray-300">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 whitespace-nowrap">Total Paid</span>
                <span className="text-lg font-bold text-gray-900 whitespace-nowrap">${totalPaid.toFixed(2)}</span>
              </div>
              {changeDue > 0 && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-semibold text-green-700 whitespace-nowrap">Change Due</span>
                  <span className="text-xl font-bold text-green-700 whitespace-nowrap">${changeDue.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          
          <button
            onClick={handleCompletePayment}
            disabled={!canFinalize || isProcessing}
            className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
              !canFinalize || isProcessing
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer'
            }`}
          >
            {isProcessing ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Processing...
              </>
            ) : (
              <>
                <i className="ri-check-line mr-2"></i>
                Complete Sale
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
